import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API, { resolvePhoto, formatAuthorName, readingMinutes } from '../utils/api';
import { Search, LogOut, LayoutDashboard, PenSquare, FileText, Cpu, Factory, Users, BookOpen, FlaskConical, Moon, Sun, Bookmark } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import InspiraFooter from '../components/InspiraFooter';
import TopBanner from '../components/TopBanner';
import { useSiteSettings } from '../context/SiteSettingsContext';
import useSEO from '../hooks/useSEO';
import './HomePage.css';

function formatDate(d) {
  try { return format(new Date(d), 'd MMM yyyy', { locale: id }); } catch { return ''; }
}

function getCover(a) {
  return resolvePhoto(a.photos?.[a.featuredPhoto || 0]?.url);
}

/* ── Artikel Card ── */
function ArticleCard({ article, onClick }) {
  const cat = article.category;
  const cover = getCover(article);
  return (
    <div className="art-card" onClick={onClick}>
      <div className="art-card-img-wrap">
        {cover
          ? <img className="art-card-img" src={cover} alt={article.title} loading="lazy" />
          : <div className="art-card-img art-card-img-placeholder">
              <FileText size={32} style={{ opacity: 0.2, color: 'var(--in-blue)' }} />
            </div>
        }
      </div>
      <div className="art-card-body">
        {cat && (
          <div className="art-card-cat" style={{ color: cat.color || 'var(--in-blue)' }}>
            {cat.name}
          </div>
        )}
        <div className="art-card-title">{article.title}</div>
        {article.excerpt && <div className="art-card-excerpt">{article.excerpt}</div>}
        <div className="art-card-meta">
          {article.assignedEditor?.name && <span>{article.assignedEditor.name}</span>}
          {article.assignedEditor?.name && article.publishedAt && <span className="art-card-meta-dot" />}
          {article.publishedAt && <span>{formatDate(article.publishedAt)}</span>}
        </div>
      </div>
    </div>
  );
}

/* icon map for heroTopics */
const TOPIC_ICONS = { FlaskConical, BookOpen, Cpu, Users, Factory, PenSquare };

/* ── Site Brand Name — adaptable 1-3 kata ── */
function SiteBrandName({ name = '', inline = false, className = '' }) {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);

  const renderWords = () => {
    if (words.length === 0) return null;

    if (words.length === 1) {
      // 1 kata: teks putih di badge biru-ke-orange gradient
      return (
        <span className="in-nav-brand-single">
          {words[0]}
        </span>
      );
    }

    // 2 kata: kata1 biru, kata2 orange badge
    // 3+ kata: kata1 biru, kata2 orange badge, kata3+ biru
    return <>
      <span style={{ color: 'var(--in-blue)' }}>{words[0]}</span>
      <span className="in-nav-brand-accent">{words[1]}</span>
      {words.length > 2 && <span style={{ color: 'var(--in-blue)', marginLeft: 4 }}>{words.slice(2).join(' ')}</span>}
    </>;
  };

  if (inline) {
    return (
      <span style={{ display: 'none', alignItems: 'center', gap: 0, fontWeight: 900, fontSize: 16, letterSpacing: '-0.3px' }}>
        {renderWords()}
      </span>
    );
  }
  return <span className={className}>{renderWords()}</span>;
}

/* ── Sidebar Portal (NYT style) ── */
function Navbar({ categories, activeCategory, onCategoryChange }) {
  const { user, logout, updateUser } = useAuth();
  const { settings } = useSiteSettings();
  const { theme, toggleTheme } = useTheme();
  const portalOwnedRoles = (user?.roles && user.roles.length > 1) ? user.roles : null;
  const switchPortalRole = async (r) => {
    if (r === user?.role) return;
    try {
      const res = await API.patch('/users/me/active-role', { role: r });
      updateUser(res.data.user);
    } catch {}
  };
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const debounceRef = useRef(null);
  const sidebarRef = useRef(null);

  // Saat scroll melewati logo besar, tampilkan kategori inline di bar atas
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close sidebar on outside click
  useEffect(() => {
    const fn = (e) => {
      if (open && sidebarRef.current && !sidebarRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  // Close sidebar on route change / resize
  useEffect(() => { setOpen(false); }, [activeCategory]);

  const getDash = () => {
    if (!user) return '/login';
    if (user.role === 'editor') return '/editor';
    if (user.role === 'producer') return '/producer';
    if (user.role === 'admin') return '/admin';
    return '/dashboard';
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchVal(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim()) navigate(`/?search=${encodeURIComponent(val.trim())}`);
      else navigate('/');
    }, 350);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchVal.trim()) navigate(`/?search=${encodeURIComponent(searchVal.trim())}`);
    else navigate('/');
    setOpen(false);
  };

  const today = new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      {/* ── MASTHEAD ala NYT ── */}
      {/* Bar atas (search dll) — fixed-sticky relatif ke .in-root agar menempel sepanjang scroll */}
      <div className={`in-masthead-top${scrolled ? ' scrolled' : ''}`}>
          <button className="in-masthead-search" onClick={() => setOpen(v => !v)} aria-label="Menu & Cari">
            <Search size={16} />
          </button>
          {scrolled ? (
            <nav className="in-masthead-top-cats">
              <button
                className={`in-masthead-cat${!activeCategory ? ' active' : ''}`}
                onClick={() => onCategoryChange('')}
              >Beranda</button>
              {categories.map(cat => (
                <button
                  key={cat._id}
                  className={`in-masthead-cat${activeCategory === cat._id ? ' active' : ''}`}
                  onClick={() => onCategoryChange(cat._id)}
                >{cat.name}</button>
              ))}
            </nav>
          ) : (
            <div className="in-masthead-date">{today}</div>
          )}
          <div className="in-masthead-top-actions">
            <button className="in-masthead-theme" onClick={toggleTheme} title={theme === 'dark' ? 'Mode terang' : 'Mode gelap'} aria-label="Ganti tema">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {/* Usulkan Artikel — tampil untuk reader & pengunjung (bukan editor/producer/admin) */}
            {(!user || user.role === 'reader') && (
              <Link to="/publikasi" className="in-masthead-submit" title="Usulkan artikel Anda">
                <PenSquare size={13} /> <span>Usulkan Artikel</span>
              </Link>
            )}
            {user ? (
              <Link to={getDash()} className="in-masthead-user" title="Dashboard">
                {user.avatar
                  ? <img src={user.avatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />
                  : <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--in-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{user.name?.[0] ?? '?'}</div>
                }
              </Link>
            ) : (
              <>
                <Link to="/register" className="in-masthead-subscribe">Daftar</Link>
                <Link to="/login" className="in-masthead-login">Masuk</Link>
              </>
            )}
          </div>
        </div>

      <header className="in-masthead">
        {/* Logo besar di tengah + tagline. Saat dark, pakai logo dark jika tersedia. */}
        <div className="in-masthead-brand">
          <Link to="/" aria-label={settings.siteName}>
            {(() => {
              const portalLogo = (theme === 'dark' && (settings.logoPortalDarkUrl || settings.logoDarkUrl))
                ? (settings.logoPortalDarkUrl || settings.logoDarkUrl)
                : (settings.logoPortalUrl || settings.logoUrl);
              return portalLogo
                ? <img src={portalLogo} alt={settings.siteName} className="in-masthead-logo-img" style={{ height: settings.logoPortalSize || 96 }} />
                : <>
                    <img src="/logo-inspira.png" alt={settings.siteName} className="in-masthead-logo-img" style={{ height: settings.logoPortalSize || 96 }}
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline-flex'; }} />
                    <SiteBrandName name={settings.siteName} className="in-masthead-brand-name" />
                  </>;
            })()}
          </Link>
          {settings.portalTagline && (
            <div className="in-masthead-tagline" style={{ fontSize: settings.portalTaglineSize || 18 }}>
              <span className="in-masthead-quote">&ldquo;</span>{settings.portalTagline}<span className="in-masthead-quote">&rdquo;</span>
            </div>
          )}
        </div>
      </header>

      {/* Nav kategori horizontal — fixed-sticky relatif ke .in-root */}
      <nav className="in-masthead-nav">
          <button
            className={`in-masthead-cat${!activeCategory ? ' active' : ''}`}
            onClick={() => onCategoryChange('')}
          >Beranda</button>
          {categories.map(cat => (
            <button
              key={cat._id}
              className={`in-masthead-cat${activeCategory === cat._id ? ' active' : ''}`}
              onClick={() => onCategoryChange(cat._id)}
            >
              {cat.name}
            </button>
          ))}
        </nav>

      {/* Overlay */}
      {open && <div className="in-sidebar-overlay" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside ref={sidebarRef} className={`in-sidebar${open ? ' open' : ''}`}>
        <div className="in-sidebar-head">
          <Link to="/" className="in-sidebar-logo" onClick={() => setOpen(false)}>
            {(() => {
              const sbLogo = (theme === 'dark' && settings.logoPortalSidebarDarkUrl)
                ? settings.logoPortalSidebarDarkUrl
                : (settings.logoPortalSidebarUrl || settings.logoDarkUrl || settings.logoUrl);
              return sbLogo
                ? <img src={sbLogo} alt={settings.siteName} style={{ height: 32, objectFit: 'contain' }} />
                : <SiteBrandName name={settings.siteName} className="in-nav-brand-name" />;
            })()}
          </Link>
          <button className="in-sidebar-close" onClick={() => setOpen(false)} aria-label="Tutup">×</button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="in-sidebar-search">
          <Search size={14} style={{ color: '#aaa', flexShrink: 0 }} />
          <input
            value={searchVal}
            onChange={handleSearchChange}
            placeholder="Cari artikel..."
            className="in-sidebar-search-input"
          />
        </form>

        {/* Kategori */}
        <div className="in-sidebar-section-title">Topik</div>
        <nav className="in-sidebar-cats">
          <button
            className={`in-sidebar-cat${!activeCategory ? ' active' : ''}`}
            onClick={() => { onCategoryChange(''); setOpen(false); }}
          >Semua</button>
          {categories.map(cat => (
            <button
              key={cat._id}
              className={`in-sidebar-cat${activeCategory === cat._id ? ' active' : ''}`}
              onClick={() => { onCategoryChange(cat._id); setOpen(false); }}
            >
              {cat.name}
            </button>
          ))}
        </nav>

        {/* User menu */}
        <div className="in-sidebar-section-title" style={{ marginTop: 16 }}>Akun</div>
        {user ? (
          <div className="in-sidebar-user-section">
            <div className="in-sidebar-user-info">
              {user.avatar
                ? <img src={user.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--in-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' }}>{user.name?.[0]}</div>
              }
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--in-text)' }}>{user.name}</div>
                <div style={{ fontSize: 11, color: 'var(--in-text-faint)', textTransform: 'capitalize' }}>{user.role}</div>
              </div>
            </div>
            {portalOwnedRoles && (
              <div style={{ padding: '4px 16px 10px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--in-text-faint)', marginBottom: 6 }}>Ganti Peran</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {portalOwnedRoles.map(r => (
                    <button key={r} onClick={() => switchPortalRole(r)}
                      style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize',
                        background: r === user.role ? 'var(--in-orange)' : 'var(--in-bg-3)',
                        color: r === user.role ? '#fff' : 'var(--in-text-muted)',
                        border: '1px solid ' + (r === user.role ? 'var(--in-orange)' : 'var(--in-border)') }}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {[
              { icon: <LayoutDashboard size={14} />, label: 'Dashboard', to: getDash() },
              { icon: <Users size={14} />, label: 'Profil Saya', to: '/profil' },
              { icon: <Bookmark size={14} />, label: 'Bookmark Saya', to: '/bookmark' },
              ...(user?.role === 'reader' ? [{ icon: <PenSquare size={14} />, label: 'Kirim Artikel', to: '/publikasi' }] : []),
            ].map(item => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="in-sidebar-link">
                {item.icon} {item.label}
              </Link>
            ))}
            <button onClick={() => { logout(); setOpen(false); navigate('/'); }} className="in-sidebar-link in-sidebar-logout">
              <LogOut size={14} /> Keluar
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 16px' }}>
            <Link to="/login" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} style={{ textAlign: 'center' }}>Masuk</Link>
            <Link to="/register" className="btn btn-primary btn-sm" onClick={() => setOpen(false)} style={{ textAlign: 'center' }}>Daftar</Link>
          </div>
        )}
      </aside>
    </>
  );
}


/* ── Hero Section ── */
function HeroSection({ user }) {
  const { settings } = useSiteSettings();
  const titleLines = (settings.heroTitle || '').split('\n');
  return (
    <div className="in-hero">
      <div className="in-hero-inner">
        <div className="in-hero-badge">
          <span className="in-hero-badge-dot" />
          {settings.heroBadgeText}
        </div>
        <h1 className="in-hero-title">
          {titleLines[0]}{titleLines.length > 1 ? <br /> : null}
          {titleLines.length > 1 && <span className="in-hero-title-accent">{titleLines.slice(1).join(' ')}</span>}
        </h1>
        <p className="in-hero-desc">{settings.heroSubtitle}</p>
        <div className="in-hero-cta">
          {user ? (
            <>
              {user.role === 'reader' && (
                <Link to="/publikasi" className="btn btn-orange btn-lg">Kirim Artikel</Link>
              )}
              <Link to={user.role === 'reader' ? '/dashboard' : `/${user.role}`} className="btn btn-secondary btn-lg">Dashboard</Link>
            </>
          ) : (
            <>
              <Link to="/register" className="btn btn-orange btn-lg">{settings.heroCtaPrimary}</Link>
              <Link to="/login" className="btn btn-secondary btn-lg">{settings.heroCtaSecondary}</Link>
            </>
          )}
        </div>
        <div className="in-hero-tags">
          {(settings.audienceTags || []).map(t => (
            <span key={t} className="in-hero-tag">{t}</span>
          ))}
        </div>
      </div>
      <div className="in-hero-visual">
        <div className="in-hero-card-float">
          <div className="in-hero-topic-grid">
            {(settings.heroTopics || []).map(item => {
              const Icon = TOPIC_ICONS[item.icon];
              return (
                <div key={item.label} className="in-hero-topic-item">
                  <div className="in-hero-topic-icon">{Icon ? <Icon size={20} /> : null}</div>
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { setPage(1); fetchArticles(1, activeCategory, true); }, [activeCategory, search]);

  const fetchCategories = async () => {
    try {
      const { data } = await API.get('/categories');
      setCategories(data.categories || []);
    } catch {}
  };

  const fetchArticles = async (p = 1, cat = '', reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 12 });
      if (cat) params.set('category', cat);
      if (search) params.set('search', search);
      const { data } = await API.get(`/articles/feed?${params}`);
      setArticles(prev => reset ? (data.articles || []) : [...prev, ...(data.articles || [])]);
      setHasMore(p < data.pages);
    } catch {} finally { setLoading(false); }
  };

  const goArticle = (a) => {
    // Artikel publik dapat dibaca tanpa login
    navigate(`/berita/${a.slug || a._id}`);
  };

  const { settings } = useSiteSettings();
  useSEO({
    title: search ? `Hasil pencarian: ${search}` : null,
    description: settings.siteDescription,
    siteName: settings.siteName,
    url: settings.siteUrl || undefined,
    seo: settings.seo || {},
  });

  // JSON-LD Organization + WebSite (homepage)
  useEffect(() => {
    const seo = settings.seo || {};
    const base = (seo.canonicalBase || settings.siteUrl || window.location.origin).replace(/\/$/, '');
    const data = [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: seo.organizationName || settings.siteName,
        url: base,
        logo: seo.organizationLogo || settings.logoPortalUrl || settings.logoUrl || `${base}/logo-inspira.png`,
        ...(Array.isArray(settings.socialLinks) && settings.socialLinks.length ? { sameAs: settings.socialLinks.map(l => l.url).filter(Boolean) } : {}),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: settings.siteName,
        url: base,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${base}/?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ];
    let script = document.getElementById('home-json-ld');
    if (!script) {
      script = document.createElement('script');
      script.id = 'home-json-ld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => { script?.remove(); };
  }, [settings.siteName, settings.siteUrl, settings.seo, settings.socialLinks, settings.logoPortalUrl, settings.logoUrl]);

  const hero = articles[0];
  const secondary = articles.slice(1, 4);   // kolom kiri — headline teks
  const opinion = articles.slice(4, 8);     // kolom kanan — terbaru/opini
  const rest = articles.slice(8);           // section di bawah

  // Kelompokkan sisa artikel per kategori untuk feed multi-section ala NYT.
  // Kategori dengan >=3 artikel jadi section sendiri; sisanya masuk "Artikel Lainnya".
  const buildSections = (list) => {
    const byCat = new Map();
    const others = [];
    for (const a of list) {
      const key = a.category?._id;
      if (!key) { others.push(a); continue; }
      if (!byCat.has(key)) byCat.set(key, { name: a.category.name, color: a.category.color, items: [] });
      byCat.get(key).items.push(a);
    }
    const sections = [];
    for (const { name, color, items } of byCat.values()) {
      if (items.length >= 3) sections.push({ title: name, color, items });
      else others.push(...items);
    }
    if (others.length) sections.push({ title: 'Artikel Lainnya', color: null, items: others });
    return sections;
  };
  const sections = !search ? buildSections(rest) : [];

  // Layout portal dari admin (urutan + visibilitas section)
  const layout = (settings.portalLayout && settings.portalLayout.length)
    ? settings.portalLayout
    : [{ key: 'hero', visible: true }, { key: 'sections', visible: true }, { key: 'loadmore', visible: true }];
  const layoutVisible = (key) => {
    const item = layout.find(l => l.key === key);
    return item ? item.visible !== false : true;
  };
  const layoutOrder = (key) => {
    const idx = layout.findIndex(l => l.key === key);
    return idx === -1 ? 99 : idx;
  };
  const showHero = !search && !loading && articles.length > 0;

  return (
    <div className="in-root">
      <TopBanner />
      <Navbar
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={(id) => { setActiveCategory(id); setPage(1); }}
      />


      {/* Feed — langsung tampil ala NYT, tanpa hero marketing */}
      <div className="in-feed-wrap in-feed-wrap--notop">
        {search && (
          <div className="in-search-info">
            Hasil untuk: <strong>"{search}"</strong> · {articles.length} artikel ditemukan
          </div>
        )}

        {loading && articles.length === 0 ? (
          <div className="skeleton-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="sk-card">
                <div className="sk-img shimmer" />
                <div className="sk-body">
                  <div className="sk-line sk-w-40 shimmer" />
                  <div className="sk-line shimmer" style={{ height: 18, marginBottom: 8 }} />
                  <div className="sk-line sk-w-80 shimmer" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="in-empty">
            {/* Features saat kosong */}
            {!search && !activeCategory && (
              <div className="in-features">
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                  <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--in-text)', marginBottom: 10 }}>
                    Cara Kerja Platform
                  </h2>
                  <p style={{ fontSize: 15, color: 'var(--in-text-muted)' }}>Dari ide ke publikasi dalam tiga langkah</p>
                </div>
                <div className="in-features-grid">
                  {[
                    { step: '01', title: 'Kirim Ringkasan', desc: 'Ceritakan fakta utama menggunakan format 5W+1H. Tidak perlu menjadi penulis profesional.' },
                    { step: '02', title: 'Proses Editorial', desc: 'Tim editor kami menyusun dan menyempurnakan tulisan Anda menjadi artikel terstruktur.' },
                    { step: '03', title: 'Publikasi & Diseminasi', desc: 'Artikel diterbitkan dan dapat diakses oleh seluruh komunitas Inspira.' },
                  ].map(f => (
                    <div key={f.step} className="in-feature-card">
                      <div className="in-feature-step">{f.step}</div>
                      <div className="in-feature-title">{f.title}</div>
                      <div className="in-feature-desc">{f.desc}</div>
                    </div>
                  ))}
                </div>
                {!user && (
                  <div style={{ textAlign: 'center', marginTop: 40 }}>
                    <Link to="/register" className="btn btn-primary btn-lg">Daftar & Mulai Berkontribusi</Link>
                  </div>
                )}
              </div>
            )}
            {(search || activeCategory) && (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.2 }}>
                  <FileText size={48} />
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--in-text)' }}>
                  Belum ada artikel
                </div>
                <div style={{ fontSize: 14, color: 'var(--in-text-muted)' }}>
                  {search ? 'Coba kata kunci yang berbeda.' : 'Topik ini belum memiliki artikel.'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* ── HERO 3-KOLOM ala NYT ── */}
            {showHero && layoutVisible('hero') && (
              <div className="in-nyt-top" style={{ order: layoutOrder('hero') }}>
                {/* Kolom kiri — daftar headline teks */}
                <div className="in-nyt-left">
                  {secondary.map(a => (
                    <div key={a._id} className="in-nyt-headline" onClick={() => goArticle(a)}>
                      {a.category && (
                        <div className="in-nyt-kicker" style={{ color: a.category.color || 'var(--in-blue)' }}>
                          {a.category.name}
                        </div>
                      )}
                      <div className="in-nyt-headline-title">{a.title}</div>
                      {a.excerpt && <div className="in-nyt-headline-excerpt">{a.excerpt}</div>}
                      <div className="in-nyt-readtime">{a.readingTime || readingMinutes(a.excerpt)} MIN BACA</div>
                    </div>
                  ))}
                </div>

                {/* Kolom tengah — artikel utama berfoto besar */}
                <div className="in-nyt-center" onClick={() => goArticle(hero)}>
                  {getCover(hero)
                    ? <img className="in-nyt-center-img" src={getCover(hero)} alt={hero.title} />
                    : <div className="in-nyt-center-img in-hero-feed-img-placeholder">
                        <FileText size={48} style={{ opacity: 0.15, color: 'var(--in-blue)' }} />
                      </div>
                  }
                  {hero.category && (
                    <div className="in-nyt-kicker" style={{ color: hero.category.color || 'var(--in-blue)' }}>
                      {hero.category.name}
                    </div>
                  )}
                  <div className="in-nyt-center-title">{hero.title}</div>
                  {hero.excerpt && <div className="in-nyt-center-excerpt">{hero.excerpt}</div>}
                  <div className="in-nyt-center-meta">
                    {hero.assignedEditor?.name && <span>Oleh {formatAuthorName(hero.assignedEditor.name)}</span>}
                    <span>{hero.readingTime || readingMinutes(hero.excerpt)} MIN BACA</span>
                  </div>
                </div>

                {/* Kolom kanan — Terbaru / Opini bergaris */}
                {opinion.length > 0 && (
                  <div className="in-nyt-right">
                    <div className="in-nyt-right-head">Terbaru</div>
                    {opinion.map(a => (
                      <div key={a._id} className="in-nyt-opinion" onClick={() => goArticle(a)}>
                        {a.assignedEditor?.name && (
                          <div className="in-nyt-opinion-author">{formatAuthorName(a.assignedEditor.name)}</div>
                        )}
                        <div className="in-nyt-opinion-title">{a.title}</div>
                        <div className="in-nyt-readtime">{a.readingTime || readingMinutes(a.excerpt)} MIN BACA</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Hasil pencarian — grid tunggal */}
            {search && articles.length > 0 && (
              <div className="in-articles-grid">
                {articles.map(a => (
                  <ArticleCard key={a._id} article={a} onClick={() => goArticle(a)} />
                ))}
              </div>
            )}

            {/* Feed multi-section ala NYT — dikelompokkan per kategori */}
            {!search && layoutVisible('sections') && (
              <div style={{ order: layoutOrder('sections') }}>
                {sections.map((sec, si) => (
                  <section key={si} className="in-feed-section">
                    <div className="in-feed-header">
                      <div className="in-feed-title" style={sec.color ? { borderBottomColor: sec.color, color: sec.color } : undefined}>
                        {sec.title}
                      </div>
                    </div>
                    <div className="in-articles-grid">
                      {sec.items.map(a => (
                        <ArticleCard key={a._id} article={a} onClick={() => goArticle(a)} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {/* Muat lebih banyak — di akhir feed */}
            {!search && hasMore && layoutVisible('loadmore') && (
              <div className="in-loadmore-row" style={{ order: layoutOrder('loadmore') }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => { const n = page + 1; setPage(n); fetchArticles(n, activeCategory, false); }}
                  disabled={loading}
                >
                  {loading ? 'Memuat...' : 'Muat Lebih Banyak'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA untuk non-user */}
      {!user && articles.length > 0 && (
        <div className="in-cta">
          <div className="in-cta-inner">
            <h2 className="in-cta-title">Punya karya yang ingin Anda dokumentasikan?</h2>
            <p className="in-cta-sub">Bergabung dan bagikan inovasi Anda kepada komunitas Inspira.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" className="btn btn-orange btn-lg">Daftar Sekarang</Link>
              <Link to="/login" className="btn btn-secondary btn-lg">Masuk</Link>
            </div>
          </div>
        </div>
      )}

      <InspiraFooter
        categories={categories}
        onCategoryClick={(id) => { setActiveCategory(id); window.scrollTo(0, 0); }}
      />
    </div>
  );
}
